'use client';

import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function useUrlParams<T extends Record<string, any>>() {
  const history = useHistory();
  const urlSearchParams = new URLSearchParams(history.location.search);

  const searchParams = Object.fromEntries(urlSearchParams) as Partial<
    Record<keyof T, string>
  >;

  function setSearchParams(
    params: Partial<Record<keyof T, string | undefined>>,
  ) {
    const newParams = new URLSearchParams(urlSearchParams);

    for (const key in params) {
      const value = params[key];

      if (value) {
        newParams.set(key, value);
      } else if (!value && urlSearchParams.get(key)) {
        newParams.delete(key);
      }
    }

    history.push(`?${newParams.toString()}`);
  }

  const removeSearchParams = useCallback(
    (keys: (keyof T)[]) => {
      const newParams = new URLSearchParams();

      const search = new URLSearchParams(window.location.search);

      for (const key in Object.fromEntries(search)) {
        if (!keys.includes(key)) {
          const value = search.get(key);

          newParams.append(key, String(value));
        }
      }

      history.push(`?${newParams.toString()}`);
    },
    [searchParams],
  );

  function replaceSearchParams(
    params: Partial<Record<keyof T, string | undefined>>,
  ) {
    const newParams = new URLSearchParams();

    for (const key in params) {
      const value = params[key];

      if (value) {
        newParams.append(key, value);
      }
    }

    history.push(`?${newParams.toString()}`);
  }

  function clearSearchParams() {
    history.push('?');
  }

  return {
    searchParams,
    setSearchParams,
    removeSearchParams,
    replaceSearchParams,
    clearSearchParams,
  };
}
